import {Alert, Autocomplete, Chip, IconButton, Stack, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import {useTranslation} from "react-i18next";
import {useState} from "react";
import {useMutation} from "react-query";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {SimphonyService} from "crqlar-react-common/dist/services/integrations/simphony/simphony.service";
import {
    SimphonyConfig,
    SimphonyRevenueCenter
} from "crqlar-react-common/dist/models/integrations/simphony/simphony.config";

interface Props {
    /** `rvcRef` (as a string key) to SectorV3 id. */
    value: { [rvcRef: string]: string | undefined }
    onChange: (value: { [rvcRef: string]: string | undefined }) => void
    sectors: Sector[]
    /** Terminal status can only be probed once the config has been saved. */
    configId?: string
    /**
     * The config as it stands in the form, used to look revenue centres up from Simphony.
     *
     * Passed whole rather than by id because the picker has to work before the connector is saved —
     * that is exactly when someone is choosing outlets for the first time.
     */
    config: SimphonyConfig
    readOnly?: boolean
}

/**
 * Maps Simphony revenue centers to crqlar sectors.
 *
 * This control decides what the connector reads: **only the revenue centers listed here are
 * synced**, and an empty map means nothing syncs at all. That is why the empty state is an explicit
 * warning rather than a blank table — a silently idle connector is the failure mode this screen
 * exists to prevent.
 *
 * The revenue-center level has no analogue in the PMS connectors: one hotel is several revenue
 * centers (restaurant, bar, spa), and a bare `rvcRef` is unique only within a location.
 */
const RevenueCenterMapping = ({value, onChange, sectors, configId, config, readOnly}: Props) => {

    const {t} = useTranslation()

    const [terminalStatus, setTerminalStatus] = useState<{ [rvcRef: string]: boolean } | undefined>()

    const entries = Object.entries(value || {})

    const checkTerminals = useMutation(['checkSimphonyPosTerminals', configId], async () => {
        if (!configId) return
        setTerminalStatus(await SimphonyService.checkPosTerminals(configId))
    })

    // Oracle's ISV Readiness Checklist asks integrators to read the hierarchy rather than have a
    // customer type codes by hand. On demand rather than on mount: it needs working credentials and
    // a chosen location, neither of which exists when a new connector's screen first opens.
    const [revenueCenters, setRevenueCenters] = useState<SimphonyRevenueCenter[]>([])

    const loadRevenueCenters = useMutation(['simphonyRevenueCenters', configId], async () => {
        setRevenueCenters(await SimphonyService.listRevenueCenters(config, config.credentials?.locRef))
    })

    const labelFor = (rvcRef: string) => {
        const match = revenueCenters.find(rc => String(rc.rvcRef) === rvcRef)
        return match?.name ? `${rvcRef} \u2014 ${match.name}` : rvcRef
    }

    const updateKey = (oldKey: string, newKey: string) => {
        const next: { [k: string]: string | undefined } = {}
        // Rebuilt in order so renaming a key does not move the row under the user's cursor.
        entries.forEach(([k, v]) => {
            next[k === oldKey ? newKey : k] = v
        })
        onChange(next)
    }

    const updateSector = (key: string, sectorId?: string) => {
        onChange({...value, [key]: sectorId})
    }

    const removeRow = (key: string) => {
        const next = {...value}
        delete next[key]
        onChange(next)
    }

    const addRow = () => {
        onChange({...value, '': undefined})
    }

    const terminalChip = (rvcRef: string) => {
        if (!terminalStatus || terminalStatus[rvcRef] === undefined) return null

        const online = terminalStatus[rvcRef]

        return (
            <Tooltip
                title={online
                    ? t('A POS workstation in this revenue center is connected.')
                    : t('No connected POS workstation. Checks cannot be read until the property powers one on — this is not a crqlar or credentials problem.')}
            >
                <Chip
                    size={'small'}
                    color={online ? 'success' : 'warning'}
                    variant={'outlined'}
                    label={online ? t('Terminal online') : t('Terminal offline')}
                />
            </Tooltip>
        )
    }

    return (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Revenue centers')}
            </Typography>

            <Alert variant={'outlined'} severity={'info'} sx={{textAlign: 'start'}}>
                {t('Only the revenue centers listed here are synced. Load them from Simphony to pick by name rather than by number.')}
            </Alert>

            <Stack direction={'row'} gap={1}>
                <Button
                    id={'load-revenue-centers'}
                    variant={'outlined'}
                    size={'small'}
                    disabled={readOnly || loadRevenueCenters.isLoading || !config.credentials?.locRef}
                    onClick={() => loadRevenueCenters.mutate()}
                >
                    {loadRevenueCenters.isLoading
                        ? <CircularProgress size={18}/>
                        : t('Load revenue centers from Simphony')}
                </Button>
            </Stack>

            {loadRevenueCenters.isSuccess && revenueCenters.length === 0 && (
                <Alert variant={'outlined'} severity={'warning'} sx={{textAlign: 'start'}}>
                    {t('Simphony returned no revenue centers for this property. Check the credentials and the selected location above.')}
                </Alert>
            )}

            {entries.length === 0 && (
                <Alert variant={'outlined'} severity={'warning'} sx={{textAlign: 'start'}}>
                    {t('No revenue centers mapped, so nothing will sync. Add at least one.')}
                </Alert>
            )}

            {entries.map(([rvcRef, sectorId], index) => (
                <Stack key={index} direction={'row'} gap={1} alignItems={'center'}>
                    <Autocomplete
                        id={`rvc-ref-${index}`}
                        freeSolo
                        options={revenueCenters}
                        // freeSolo keeps manual entry working. The list needs saved, working
                        // credentials to load, and a property mid-setup may not have them yet.
                        getOptionLabel={(option: SimphonyRevenueCenter | string) =>
                            typeof option === 'string'
                                ? option
                                : `${option.rvcRef} \u2014 ${option.name || ''}`.trim()}
                        value={labelFor(rvcRef)}
                        disabled={readOnly}
                        sx={{width: 260}}
                        onChange={(_, option) => updateKey(
                            rvcRef,
                            typeof option === 'string' ? option : String(option?.rvcRef ?? '')
                        )}
                        onInputChange={(_, text, reason) => {
                            if (reason === 'input') updateKey(rvcRef, text)
                        }}
                        renderInput={(params) => (
                            <TextField {...params} size={'small'} label={t('Revenue center')}/>
                        )}
                    />

                    <Autocomplete
                        id={`rvc-sector-${index}`}
                        options={sectors}
                        getOptionLabel={(option: Sector) => option.name || ''}
                        value={sectors.find(s => s.id === sectorId) || null}
                        disabled={readOnly}
                        fullWidth
                        renderInput={(params) => (
                            <TextField {...params} size={'small'} label={t('Sector')} fullWidth/>
                        )}
                        onChange={(e, sector) => updateSector(rvcRef, sector?.id)}
                    />

                    {terminalChip(rvcRef)}

                    <IconButton
                        aria-label={t('Remove revenue center')}
                        disabled={readOnly}
                        onClick={() => removeRow(rvcRef)}
                    >
                        <DeleteOutlineIcon/>
                    </IconButton>
                </Stack>
            ))}

            <Stack direction={'row'} gap={1}>
                <Button
                    startIcon={<AddIcon/>}
                    disabled={readOnly}
                    onClick={addRow}
                >
                    {t('Add revenue center')}
                </Button>

                {configId && (
                    <Button
                        onClick={() => checkTerminals.mutate()}
                        disabled={checkTerminals.isLoading}
                        startIcon={checkTerminals.isLoading ? <CircularProgress size={16}/> : undefined}
                    >
                        {t('Check POS terminals')}
                    </Button>
                )}
            </Stack>
        </Stack>
    )
}

export default RevenueCenterMapping;
